<template>
  <div>
    <div
      v-if="!turns.length"
      class="empty-turns text-center"
    >
      <p>{{ $t('form.emptyTurns') }}</p>
      <v-btn
        color="secondary"
        outlined
        @click="goToTurns"
      >
        {{ $t('button.createTurn') }}
      </v-btn>
    </div>
    <div v-else>
      <validation-observer ref="formRecurrence">
        <v-row>
          <v-col sm="6">
            <validation-provider
              v-slot="{ errors, valid }"
              :rules="{ required: true }"
              name="recurrence"
            >
              <v-select
                v-model="type"
                :label="$t('fields.recurrence')"
                :messages="$t('fields.recurrenceDescription')"
                :no-data-text="$t('noDataText')"
                :items="types"
                :hide-details="valid"
                :error-messages="errors"
                item-text="name"
                item-value="code"
                outlined
                dense
              >
              </v-select>
            </validation-provider>
          </v-col>
          <v-col sm="6">
            <validation-provider
              v-slot="{ errors, valid }"
              :rules="{ required: true }"
              name="weekNumber"
            >
              <v-select
                v-model="weekNumber"
                :label="$t('fields.weekNumber')"
                :messages="$t('fields.weekNumberDescription')"
                :no-data-text="$t('noDataText')"
                :items="weekNumbers"
                :hide-details="valid"
                :error-messages="errors"
                item-text="name"
                item-value="code"
                outlined
                dense
              >
              </v-select>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row>
          <v-col sm="6">
            <validation-provider
              v-slot="{ errors, valid }"
              :rules="{ required: true }"
              name="turn"
            >
              <v-select
                v-model="turn"
                :label="$t('fields.turn')"
                :no-data-text="$t('noDataText')"
                :items="turns"
                :hide-details="valid"
                :error-messages="errors"
                item-text="description"
                item-value="id"
                outlined
                dense
              >
              </v-select>
            </validation-provider>
          </v-col>
          <v-col sm="3">
            <v-text-field
              v-model="startHour"
              :label="$t('fields.turnInitialHour')"
              outlined
              dense
              readonly
            ></v-text-field>
          </v-col>
          <v-col sm="3">
            <v-text-field
              v-model="endHour"
              :label="$t('fields.turnFinalHour')"
              outlined
              dense
              readonly
            ></v-text-field>
          </v-col>
        </v-row>
      </validation-observer>
      <v-row>
        <v-col sm="12 pt-0">
          <weekly-days
            :label="$t('fields.weeklyDays')"
            :message="$t('fields.weeklyDaysDescription')"
            :selectable="true"
            :days="selectedDays"
          />
        </v-col>
      </v-row>
      <v-row class="mt-6">
        <v-col sm="12">
          <job-vacancy
            ref="jobVacancyComponent"
            :vacancies="vacancies"
            :place-id="placeId"
            :department-id="departmentId"
          />
        </v-col>
      </v-row>
      <v-row class="mt-8">
        <v-spacer></v-spacer>
        <v-col sm="6">
          <v-btn
            block
            color="secondary"
            outlined
            @click="close"
          >
            {{ $t('button.cancel') }}
          </v-btn>
        </v-col>
        <v-col sm="6">
          <v-btn
            block
            color="primary"
            @click="save"
          >
            {{ $t('button.save') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<style scoped>
  .empty-turns {
    margin: 3rem auto !important;
  }

  .empty-turns p {
    font-size: 0.9rem;
  }
</style>

<script>
import WeeklyDays from '../../components/WeeklyDays.vue';
import JobVacancy from '../../components/JobVacancy.vue';

export default {
  components: {
    WeeklyDays,
    JobVacancy,
  },

  props: {
    placeId: {
      type: String,
      required: true,
    },

    departmentId: {
      type: String,
      required: true,
    },

    activityId: {
      type: String,
      required: true,
    },

    close: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      types: [],
      weekNumbers: [],
      turns: [],
      type: null,
      weekNumber: null,
      turn: null,
      startHour: null,
      endHour: null,
      defaultAmountHour: null,
      selectedDays: [],
      vacancies: [],
    };
  },

  watch: {
    turn() {
      const turn = this.turns.find((item) => item.id === this.turn);
      this.startHour = turn.initial_hour;
      this.endHour = turn.final_hour;
      this.$refs.jobVacancyComponent.fillDefaultPricePerHour(turn.amount_hour);
    },
  },

  async mounted() {
    this.fillTypes();
    this.fillWeekNumbers();
    await this.fillTurns();
  },

  methods: {
    async save() {
      if (!(await this.$refs.formRecurrence.validate())) {
        return;
      }

      await this.$api.recurrence.create({
        type: this.type,
        days: this.selectedDays,
        start_week: this.weekNumber,
        start_hour: this.startHour,
        end_hour: this.endHour,
        customers: this.vacancies,
        place_id: this.placeId,
        department_id: this.departmentId,
        activity_id: this.activityId,
      });

      this.$store.dispatch('NOTIFICATION_INFO', 'Recorrencia cadastrada');
      this.$emit('on-change');
      this.close();
    },

    async fillTurns() {
      this.turns = (await this.$api.turn.getMany()).data.data.filter(
        (turn) => turn.department_id === this.departmentId,
      );
    },

    fillTypes() {
      ['weekly', 'biweekly'].forEach(type => {
        this.types.push({
          code: type,
          name: this.$t(`recurrenceTypes.${type}`),
        });
      });
    },

    fillWeekNumbers() {
      [0, 1, 2, 3, 4, 5].forEach((number) => {
        this.weekNumbers.push({
          code: number,
          name: this.$t(`weekNumbers.${number}`),
        });
      });
    },

    goToTurns() {
      this.$router.push(`/turns/${this.placeId}/${this.departmentId}`);
    },
  },
};
</script>
